import * as React from "react";
import { makeStyles, Popper, TextField } from "@material-ui/core";
import { max, min, Slot } from "@clockwise/client-commons/src/constants/time-slot";
import { Autocomplete } from "@material-ui/lab";

import { StartOrEndSlot } from "@ui/working-hours";
import { fontFamilySans } from "@styles/type.styles";
import { greys } from "@styles/color.styles";

export interface AutocompleteTimeslotProps {
  type: StartOrEndSlot;
  defaultValue: Slot;
  onTimeSlotChange: (option: Slot) => void;
}

const useStyles = makeStyles({
  autocomplete: {
    // reset the large paddingRight
    // that shrinks the input
    "& > div > div": {
      paddingRight: "2px !important",
    },

    "& .MuiInputBase-input": {
      textAlign: "center",
      lineHeight: "21px",
      fontSize: 14,
      minWidth: 70,
    },

    "& .MuiInput-underline": {
      "&:hover": {
        "&:before": {
          borderBottom: 0,
        },
      },
      "&:before": {
        borderBottom: 0,
      },
    },
  },
  popper: {
    minWidth: 175,
    zIndex: 9999,

    "& > *": {
      fontSize: 14,
      fontFamily: fontFamilySans,
      color: greys.standard,
    },
  },
  listbox: {
    padding: 0,
  },
});

const minSlotSuggestions = Object.keys(min);
const maxSlotSuggestions = Object.keys(max);

export const AutocompleteTimeslot = ({
  type,
  defaultValue,
  onTimeSlotChange,
}: AutocompleteTimeslotProps) => {
  const classes = useStyles();
  let autocompleteProps: any = {};

  if (type === StartOrEndSlot.StartSlot) {
    autocompleteProps = {
      options: minSlotSuggestions,
      getOptionLabel: (option: Slot) => min[option],
      filterOptions: (options: Slot[], { inputValue }: { inputValue: string }) => {
        if (!inputValue) {
          return options;
        }

        return options.filter((opt) => min[opt].startsWith(inputValue));
      },
    };
  } else {
    autocompleteProps = {
      options: maxSlotSuggestions,
      getOptionLabel: (option: Slot) => max[option],
      filterOptions: (options: Slot[], { inputValue }: { inputValue: string }) => {
        if (!inputValue) {
          return options;
        }

        return options.filter((opt) => max[opt].startsWith(inputValue));
      },
    };
  }

  return (
    <Autocomplete
      {...autocompleteProps}
      options={autocompleteProps.options}
      popupIcon={null}
      className={classes.autocomplete}
      defaultValue={defaultValue}
      disableClearable={true}
      autoHighlight={true}
      noOptionsText={"Invalid time"}
      ListboxProps={{ className: classes.listbox }}
      PopperComponent={({ children, ...popperProps }) => {
        return (
          <Popper {...popperProps} placement={"bottom-start"} className={classes.popper}>
            {children}
          </Popper>
        );
      }}
      renderInput={({ ...params }) => {
        return <TextField {...params} fullWidth />;
      }}
      onChange={(_e, option) => onTimeSlotChange(option as Slot)}
    />
  );
};
